import React, { useEffect, useRef, useState } from "react";
import "../../VideoManager.css";

const AudioContextClass = typeof window !== "undefined" ? window.AudioContext || window.webkitAudioContext : null;

const createWavBlob = (buffer) => {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const dataLength = buffer.length * blockAlign;
  const bufferLength = 44 + dataLength;
  const arrayBuffer = new ArrayBuffer(bufferLength);
  const view = new DataView(arrayBuffer);

  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i += 1) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + dataLength, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, "data");
  view.setUint32(40, dataLength, true);

  let offset = 44;
  for (let i = 0; i < buffer.length; i += 1) {
    for (let channel = 0; channel < numChannels; channel += 1) {
      const sample = buffer.getChannelData(channel)[i];
      const clamped = Math.max(-1, Math.min(1, sample));
      view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
      offset += bytesPerSample;
    }
  }

  return new Blob([arrayBuffer], { type: "audio/wav" });
};

const mixAudioBuffers = (base, overlay) => {
  const channels = Math.max(base.numberOfChannels, overlay.numberOfChannels);
  const length = Math.max(base.length, overlay.length);
  const sampleRate = base.sampleRate;
  const context = new AudioContextClass();
  const result = context.createBuffer(channels, length, sampleRate);

  for (let channel = 0; channel < channels; channel += 1) {
    const baseData = base.getChannelData(channel % base.numberOfChannels) || new Float32Array(base.length);
    const overlayData = overlay.getChannelData(channel % overlay.numberOfChannels) || new Float32Array(overlay.length);
    const resultData = result.getChannelData(channel);
    for (let i = 0; i < length; i += 1) {
      const sum = (baseData[i] || 0) + (overlayData[i] || 0);
      resultData[i] = Math.max(-1, Math.min(1, sum * 0.8));
    }
  }

  context.close();
  return result;
};

const sliceBuffer = (buffer, start, end) => {
  const context = new AudioContextClass();
  const frameLength = Math.max(0, Math.floor((end - start) * buffer.sampleRate));
  const newBuffer = context.createBuffer(buffer.numberOfChannels, frameLength, buffer.sampleRate);
  for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
    const sourceData = buffer.getChannelData(channel);
    const targetData = newBuffer.getChannelData(channel);
    for (let i = 0; i < frameLength; i += 1) {
      targetData[i] = sourceData[i + Math.floor(start * buffer.sampleRate)] || 0;
    }
  }
  context.close();
  return newBuffer;
};

export default function AdvancedAudioEditor() {
  const [audioBuffer, setAudioBuffer] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [eqPreset, setEqPreset] = useState("warm");
  const [compressor, setCompressor] = useState(false);
  const [selection, setSelection] = useState({ start: 0, end: 0 });
  const [overlayReady, setOverlayReady] = useState(false);
  const [mixFile, setMixFile] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
  }, [previewUrl]);

  useEffect(() => {
    if (!audioBuffer) return;
    const duration = audioBuffer.duration;
    setSelection((prev) => ({
      start: prev.start || 0,
      end: prev.end || duration,
    }));
  }, [audioBuffer]);

  const updatePreview = (buffer) => {
    if (!buffer) return;
    const wavBlob = createWavBlob(buffer);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    const url = URL.createObjectURL(wavBlob);
    setPreviewUrl(url);
    setOverlayReady(true);
  };

  const handleUpload = async (event) => {
    if (!AudioContextClass) return;
    const file = event.target.files?.[0];
    if (!file) return;
    const arrayBuffer = await file.arrayBuffer();
    const context = new AudioContextClass();
    const decoded = await context.decodeAudioData(arrayBuffer);
    context.close();
    setAudioBuffer(decoded);
    setOverlayReady(false);
    updatePreview(decoded);
  };

  const handleMixUpload = async (event) => {
    if (!AudioContextClass || !audioBuffer) return;
    const file = event.target.files?.[0];
    if (!file) return;
    const arrayBuffer = await file.arrayBuffer();
    const context = new AudioContextClass();
    const decoded = await context.decodeAudioData(arrayBuffer);
    context.close();
    const mixed = mixAudioBuffers(audioBuffer, decoded);
    setAudioBuffer(mixed);
    setMixFile(file.name);
    updatePreview(mixed);
  };

  const applyTrim = () => {
    if (!audioBuffer) return;
    const trimmed = sliceBuffer(audioBuffer, selection.start, selection.end);
    setAudioBuffer(trimmed);
    updatePreview(trimmed);
  };

  const applyCut = () => {
    if (!audioBuffer || selection.end <= selection.start) return;
    const context = new AudioContextClass();
    const firstPart = sliceBuffer(audioBuffer, 0, selection.start);
    const secondPart = sliceBuffer(audioBuffer, selection.end, audioBuffer.duration);
    const length = firstPart.length + secondPart.length;
    const mixed = context.createBuffer(audioBuffer.numberOfChannels, length, audioBuffer.sampleRate);
    for (let channel = 0; channel < audioBuffer.numberOfChannels; channel += 1) {
      mixed.copyToChannel(firstPart.getChannelData(channel), channel, 0);
      mixed.copyToChannel(secondPart.getChannelData(channel), channel, firstPart.length);
    }
    context.close();
    setAudioBuffer(mixed);
    updatePreview(mixed);
  };

  const downloadCurrent = () => {
    if (!previewUrl) return;
    const anchor = document.createElement("a");
    anchor.href = previewUrl;
    anchor.download = "gcc-audio-edit.wav";
    anchor.click();
  };

  const duration = audioBuffer?.duration || 0;

  return (
    <section className="studio-panel">
      <div className="studio-panel__header">
        <h2>Advanced Audio Studio</h2>
        <p>Full trim/cut controls, selection playback, and mixing layers aim to mirror classic DAWs.</p>
      </div>

      <div className="studio-panel__dual-grid audio-lab__layout">
        <div className="studio-panel__controls-column">
          <div className="studio-panel__section-card">
            <h4>File + selection</h4>
            <input type="file" accept="audio/*" onChange={handleUpload} />
            <label>
              <span>Start (seconds)</span>
              <input
                type="number"
                min={0}
                max={duration}
                value={selection.start}
                onChange={(event) =>
                  setSelection((prev) => ({ ...prev, start: Math.min(Number(event.target.value) || 0, prev.end) }))
                }
              />
            </label>
            <label>
              <span>End (seconds)</span>
              <input
                type="number"
                min={0}
                max={duration}
                value={selection.end}
                onChange={(event) =>
                  setSelection((prev) => ({ ...prev, end: Math.max(Number(event.target.value) || 0, prev.start) }))
                }
              />
            </label>
            <div className="studio-panel__preview-actions">
              <button type="button" className="studio-panel__cta" onClick={applyTrim} disabled={!audioBuffer}>
                Trim selection
              </button>
              <button type="button" className="studio-panel__cta studio-panel__cta--secondary" onClick={applyCut} disabled={!audioBuffer}>
                Cut selection
              </button>
            </div>
          </div>

          <div className="studio-panel__section-card">
            <h4>Mix & effects</h4>
            <input type="file" accept="audio/*" onChange={handleMixUpload} />
            <p className="studio-panel__helper">{mixFile ? `Mix: ${mixFile}` : "No mix layer yet."}</p>
            <label>
              <span>EQ preset</span>
              <select value={eqPreset} onChange={(event) => setEqPreset(event.target.value)}>
                <option value="warm">Warm</option>
                <option value="bright">Bright</option>
                <option value="vocal">Vocal lift</option>
              </select>
            </label>
            <label className="studio-panel__checkbox-label">
              <input
                type="checkbox"
                checked={compressor}
                onChange={(event) => setCompressor(event.target.checked)}
              />
              <span>Compressor boost</span>
            </label>
            <div className="studio-panel__helper">
              {`Preset ${eqPreset} · Compressor ${compressor ? "on" : "off"}`}
            </div>
          </div>
        </div>

        <div className="studio-panel__preview-column">
          <div className="studio-panel__audio-preview-block">
            {previewUrl ? (
              <audio controls src={previewUrl} className="studio-panel__audio-preview" ref={audioRef} />
            ) : (
              <div className="recording-studio__placeholder">Load audio to view waveform + controls.</div>
            )}
          </div>
          <div className="studio-panel__preview-actions">
            <button type="button" className="studio-panel__cta" onClick={() => audioRef.current?.play()} disabled={!previewUrl}>
              Play all
            </button>
            <button
              type="button"
              className="studio-panel__cta"
              onClick={() => {
                if (!audioRef.current) return;
                audioRef.current.currentTime = selection.start;
                audioRef.current.play();
                setTimeout(() => {
                  if (audioRef.current) audioRef.current.pause();
                }, Math.max(100, (selection.end - selection.start) * 1000));
              }}
              disabled={!previewUrl}
            >
              Play selection
            </button>
            <button type="button" className="studio-panel__cta studio-panel__cta--secondary" onClick={downloadCurrent} disabled={!previewUrl}>
              Download edit
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
